/**
 * 21 - Steal the Plans (10 §2): a fenced headquarters - the HQ building with the safe in it, the staff's
 * tents, a guard house at the gate, a staff car on the hardstanding. Six seconds at the safe takes the
 * documents. The moment the garrison is properly alarmed (alert 2) the staff start burning them, and forty
 * seconds later there is nothing left to take; until he is inside the fence, noise costs him the bonus too.
 */

import { PROPS } from '../../sim/props';
import type { Point } from '../types';
import {
  checker, clutter, countKind, guard, guardAtDoor, guardsAbout, insideRect, required, ruleOf, structure, targetsOf,
  uniformDonor,
} from './helpers';
import { RECALL } from './helpers3b';
import type { ArchetypeSpec } from './types';

const HOLD_SECONDS = 6;
const BURN_AFTER = 40;

export const stealPlans: ArchetypeSpec = {
  id: 'steal-plans',
  name: 'STEAL THE PLANS',
  nativeBiome: 'temperate',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [52, 62], d: [30, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const placed: Point[] = [];
    // The HQ stands in the northern half: the camp is between it and the gate.
    const north = { ...g.inner, z1: g.inner.z0 + (g.inner.z1 - g.inner.z0) * 0.62 };
    const hq = required(g, 'hqBuilding', { within: north, from: placed, minDist: 0, margin: 2 });
    // The safe stands against the back wall, off to one side: the building is walk-in, so it is placed, not fitted.
    const side = g.rng.chance(0.5) ? 1 : -1;
    const hx = PROPS.hqBuilding.shapes[0]?.hx ?? 5;
    const hz = PROPS.hqBuilding.shapes[0]?.hz ?? 3.5;
    const safe = g.map.add('safe', hq.x + side * (hx - 1.5), hq.z - (hz - 1.2), { target: true });

    const house = required(g, 'guardHouse', { minDist: 12, from: placed, margin: 1.4 });
    const tents = 2 + g.rng.int(2);
    for (let i = 0; i < tents; i++) structure(g, g.kit.shelter, { minDist: 8, from: placed, margin: 1.4 });
    structure(g, 'staffCar', { minDist: 7, from: placed, margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);

    // A staff officer at the HQ door, the man whose uniform opens the camp at the guard house, and the duty guards.
    guardAtDoor(g, hq, 'officer');
    guardAtDoor(g, house);
    const b = g.map.bounds('hqBuilding', hq.x, hq.z);
    if (g.threat >= 4) guard(g, b.x0 - 2.5, hq.z, -1.2);
    if (g.threat >= 7) guard(g, b.x1 + 2.5, hq.z, 1.2);
    guardsAbout(g, 1 + g.rng.int(2));

    return {
      objective: {
        kind: 'useObjects', label: 'STEAL THE PLANS', targetPropIds: [safe.id], minFraction: 1,
        holdSeconds: HOLD_SECONDS, useLabel: 'TAKE THE DOCUMENTS',
        rules: [
          { type: 'quietUntilZone', zone: { ...g.rect }, breach: 'penalty' },
          {
            type: 'alertConsequence', level: 2, delaySeconds: BURN_AFTER, effect: 'documentsBurned',
            orders: 'IF THE ALARM IS RAISED THE STAFF BURN THE PLANS: 40 SECONDS TO REACH THE SAFE',
            warning: 'THEY ARE BURNING THE PLANS — GONE IN',
            reason: 'THE PLANS WERE BURNED',
          },
          { type: 'noContactBonus' },
          RECALL,
        ],
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('steal-plans');
    const [safe] = targetsOf(def, 'safe');
    const hq = def.props.find((p) => p.kind === 'hqBuilding');
    check(safe !== undefined && def.objective.targetPropIds.length === 1, 'the safe must be the one target');
    check(safe?.target === true, 'the safe must be flagged for the strip map');
    check(hq !== undefined && countKind(def, 'hqBuilding') === 1, 'needs exactly one HQ building');
    if (safe && hq) {
      check(Math.abs(safe.x - hq.x) <= 4.5 && Math.abs(safe.z - hq.z) <= 3, 'the safe must stand inside the HQ building');
      check(insideRect(def.goalZone, hq.x, hq.z), 'the HQ must stand inside the fence');
      check(probe.breach(safe.id) === 0, 'the safe must be reachable through the open gates');
    }
    check(
      def.objective.kind === 'useObjects' && def.objective.holdSeconds === HOLD_SECONDS,
      `objective must be useObjects with a ${HOLD_SECONDS} s hold`,
    );
    const burn = ruleOf(def, 'alertConsequence');
    check(
      burn?.effect === 'documentsBurned' && burn.level === 2 && burn.delaySeconds === BURN_AFTER,
      `alert 2 must burn the documents after ${BURN_AFTER} s`,
    );
    check(
      burn?.orders !== undefined && burn.warning !== undefined && burn.reason !== undefined,
      'the burning rule must carry its orders, its countdown and its reason',
    );
    const quiet = ruleOf(def, 'quietUntilZone');
    check(quiet !== undefined && quiet.breach === 'penalty', 'strict quiet until inside the fence');
    check(countKind(def, 'guardHouse') >= 1 && uniformDonor(def, 'guardHouse'), 'a guard must stand at the guard house');
    check(def.spawns.some((s) => s.kind === 'officer' && insideRect(def.goalZone, s.x, s.z)), 'the staff officer is missing');
    return out;
  },
};
